import { PlayPauseIcon } from '@neo/primitives/audio-player'
import { Loader2 } from 'lucide-react'
import type React from 'react'
import type { RVoiceWithModelAndProvider } from '../sidebar/types'

interface Voice {
  id: string
  label?: string
  preview_url?: string | null
  meta?: {
    accent?: string
    gender?: string
  }
}

interface STTTTSConfig {
  ttsModel: string
  ttsProvider: string
}

interface VoicePlayControlProps {
  voice: RVoiceWithModelAndProvider | Voice | undefined
  sttTtsConfig?: STTTTSConfig
  isPlaying: (voiceId: string) => boolean
  loadingVoiceId: string | null
  onPlayPreview: (
    voiceId: string,
    previewUrl: string | null,
    model: string,
    provider: string,
    languageCode?: string,
  ) => Promise<void>
  onStop: () => void
  options?: {
    onClick?: (e: React.MouseEvent) => void
    showLabel?: boolean
    className?: string
  }
}

const isVoiceWithModelAndProvider = (
  voice: Voice | RVoiceWithModelAndProvider,
): voice is RVoiceWithModelAndProvider => {
  return typeof voice === 'object' && voice !== null && 'model' in voice && 'provider' in voice
}

export const VoicePlayControl: React.FC<VoicePlayControlProps> = ({
  voice,
  sttTtsConfig,
  isPlaying,
  loadingVoiceId,
  onPlayPreview,
  onStop,
  options = {},
}) => {
  if (!voice) return null

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    options.onClick?.(e)

    const voiceId = voice.id
    if (isPlaying(voiceId)) {
      onStop()
      return
    }

    let model = sttTtsConfig?.ttsModel || ''
    let provider = sttTtsConfig?.ttsProvider || ''
    if (isVoiceWithModelAndProvider(voice)) {
      model = voice.model
      provider = voice.provider
    }

    onPlayPreview(voiceId, voice.preview_url || null, model, provider, voice.meta?.accent)
  }

  return (
    <div className="flex items-center gap-2">
      {loadingVoiceId === voice.id ? (
        <div
          className={`flex shrink-0 items-center justify-center ${options.className || 'size-7'}`}
        >
          <Loader2 className={`animate-spin text-brand-text ${options.className || 'size-5'}`} />
        </div>
      ) : (
        <PlayPauseIcon
          className={`shrink-0 ${options.className || 'size-7'}`}
          playing={isPlaying(voice.id)}
          onClick={handleClick}
        />
      )}
      {options.showLabel && <span className="truncate">{voice.label}</span>}
    </div>
  )
}
